<template>
  <div class="px-16">
    <v-data-table
      hide-default-footer
      class="elevation-1"
      :headers="headers"
      :items="translations"
      :items-per-page="-1"
      :item-class="rowClass"
      :server-items-length="itemsCount"
      :options.sync="tableOptions"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-btn tile color="primary" class="link non-text-transformation" @click="editItem()">
            {{ $t('ADMIN_TRANSLATIONS_ADD_BUTTON') }}
          </v-btn>

          <v-spacer class="mx-4" />

          <div class="d-flex align-center text-body-2">
            <div class="mr-2">{{ $t('ADMIN_TABLE_RESULTS_PER_PAGE') }}</div>

            <v-select
              dense
              v-model="tableOptions.itemsPerPage"
              :items="perPageValues"
              class="admin__results-per-page"
            />
          </div>
        </v-toolbar>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-icon small class="mr-2" @click="editItem(item)">mdi-pencil</v-icon>
        <v-icon small @click="deleteItem(item)">mdi-delete</v-icon>
      </template>

      <template v-slot:no-data>
        <div class="mt-4 mb-2 text-body-2 admin__no-data">
          {{ $t('ADMIN_TABLE_NO_RESULTS') }}
        </div>

        <v-btn color="primary" class="mb-4" @click="requestTranslations">
          {{ $t('ADMIN_UPDATE') }}
        </v-btn>
      </template>

      <template v-slot:footer>
        <v-pagination class="py-4" v-model="tableOptions.page" :length="pageCount" />
      </template>
    </v-data-table>

    <v-dialog v-model="editDialog" max-width="350">
      <v-card>
        <v-card-title class="text-h3">
          {{
            isEditingItem
              ? $t('ADMIN_TRANSLATIONS_MODAL_EDIT_TITLE')
              : $t('ADMIN_TRANSLATIONS_MODAL_CREATE_TITLE')
          }}
        </v-card-title>

        <v-form ref="translationForm" v-model="isFormValid">
          <v-card-text class="pb-0">
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    outlined
                    v-model="editedItem.key"
                    :label="$t('ADMIN_TRANSLATIONS_TABLE_HEADER_KEY')"
                    :rules="keyRules"
                    :readonly="isEditingItem"
                    @focus="isEditingItem && $event.target.blur()"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    outlined
                    v-model="editedItem.valueDefault"
                    :label="$t('ADMIN_TRANSLATIONS_TABLE_HEADER_VALUE')"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-form>

        <v-card-actions class="d-flex justify-end pa-4">
          <v-btn
            tile
            outlined
            color="black"
            class="link non-text-transformation"
            @click="cancelEdit"
          >
            {{ $t('ADMIN_CANCEL') }}
          </v-btn>

          <v-btn
            tile
            color="primary"
            class="link non-text-transformation"
            :disabled="isConfirmEditDisabled"
            @click="confirmEdit"
          >
            {{ $t('ADMIN_CONFIRM') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="510">
      <v-card>
        <v-card-title class="text-h3">
          {{ $t('ADMIN_TRANSLATIONS_MODAL_DELETE_TITLE') }}
        </v-card-title>

        <v-card-actions class="d-flex justify-end pa-4">
          <v-btn
            tile
            outlined
            color="black"
            class="link non-text-transformation"
            @click="cancelDelete"
          >
            {{ $t('ADMIN_CANCEL') }}
          </v-btn>

          <v-btn tile color="primary" class="link non-text-transformation" @click="confirmDelete">
            {{ $t('ADMIN_CONFIRM') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { debounce } from '@/helpers/debounce'
import {
  REQUEST_TRANSLATIONS,
  CREATE_TRANSLATION,
  UPDATE_TRANSLATION,
  DELETE_TRANSLATION,
} from '@/store/modules/offer/actions'

export default {
  name: 'Translations',
  data() {
    return {
      perPageValues: [5, 10, 25, 50, 100],
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['key'],
        sortDesc: [false],
      },
      itemsCount: 0,
      pageCount: 1,
      headers: [
        {
          text: this.$t('ADMIN_TRANSLATIONS_TABLE_HEADER_KEY'),
          value: 'key',
          class: 'text-body-2',
        },
        {
          text: this.$t('ADMIN_TRANSLATIONS_TABLE_HEADER_VALUE'),
          value: 'valueDefault',
          class: 'text-body-2',
        },
        {
          text: this.$t('ADMIN_TABLE_HEADER_ACTIONS'),
          value: 'actions',
          align: 'center',
          sortable: false,
          class: 'text-body-2',
        },
      ],
      translations: [],
      editedIndex: -1,
      defaultItem: {
        key: '',
        valueDefault: '',
      },
      editedItem: {
        key: '',
        valueDefault: '',
      },
      editDialog: false,
      deleteDialog: false,
      keyRules: [
        valueDefault =>
          !valueDefault ||
          /^[A-Z]{1,}[A-Z0-9_]*$/.test(valueDefault) ||
          this.$t('ADMIN_TRANSLATIONS_FORM_KEY_FORMAT_ERROR'),
      ],
      isFormValid: false,
    }
  },
  computed: {
    isConfirmEditDisabled() {
      return this.editedItem.key === '' || this.editedItem.valueDefault === '' || !this.isFormValid
    },
    isEditingItem() {
      return this.editedIndex > -1
    },
  },
  methods: {
    ...mapActions('offer', [
      REQUEST_TRANSLATIONS,
      CREATE_TRANSLATION,
      UPDATE_TRANSLATION,
      DELETE_TRANSLATION,
    ]),
    ...mapActions('alerts', ['createAlert']),
    getRequestParams() {
      const params = {
        page: this.tableOptions.page - 1,
        size: this.tableOptions.itemsPerPage,
      }

      if (this.tableOptions.sortBy[0] !== undefined) {
        params.sortField = this.tableOptions.sortBy[0]
        params.sortDirection = this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC'
      }

      return params
    },
    // HACK: debounce method not to process duplicated calls on table update
    requestTranslations: debounce(async function() {
      this.translations = []

      const options = this.getRequestParams()

      const response = await this[REQUEST_TRANSLATIONS](options)

      if (response.status === 200) {
        this.translations = response.data.data

        this.itemsCount = response.data.metadata.totalItems
        this.pageCount = response.data.metadata.totalPages
      } else {
        this.createAlert({
          id: 'adminTranslationsRequest',
          type: 'error',
          contentKey: 'ADMIN_ALERT_TEXT',
          dismissible: false,
        })
      }
    }),
    editItem(item) {
      this.editedIndex = this.translations.indexOf(item)
      this.editedItem = { ...item }
      this.editDialog = true
    },
    cancelEdit() {
      this.editDialog = false
      this.$refs.translationForm?.resetValidation()
    },
    async createTranslation() {
      const response = await this[CREATE_TRANSLATION](this.editedItem)

      if (response.status === 201) {
        this.requestTranslations()
        this.cancelEdit()
      } else {
        this.createAlert({
          id: 'adminTranslationsCreate',
          type: 'error',
          contentKey: 'ADMIN_ALERT_TEXT',
          dismissible: false,
        })
      }
    },
    async updateTranslation() {
      const response = await this[UPDATE_TRANSLATION](this.editedItem)

      if (response.status === 201) {
        this.requestTranslations()
        this.cancelEdit()
      } else {
        // TODO: define possible errors and show different alerts dependeing on response
        this.createAlert({
          id: 'adminTranslationsUpdate',
          type: 'error',
          contentKey: 'ADMIN_ALERT_TEXT',
          dismissible: false,
        })
      }
    },
    confirmEdit() {
      if (this.isEditingItem) {
        this.updateTranslation()
      } else {
        this.createTranslation()
      }
    },
    deleteItem(item) {
      this.editedItem = { ...item }
      this.deleteDialog = true
    },
    cancelDelete() {
      this.deleteDialog = false
    },
    async confirmDelete() {
      const response = await this[DELETE_TRANSLATION](this.editedItem)

      if (response.status === 200) {
        this.requestTranslations()
      } else {
        this.createAlert({
          id: 'adminTranslationsDelete',
          type: 'error',
          contentKey: 'ADMIN_ALERT_TEXT',
          dismissible: false,
        })
      }
      this.cancelDelete()
    },
    rowClass() {
      return 'text-body-2'
    },
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.cancelEdit()
      }
    },
    tableOptions: {
      deep: true,
      handler() {
        this.requestTranslations()
      },
    },
  },
}
</script>

<style scoped lang="scss">
.admin {
  &__no-data {
    color: #444444;
  }
  &__results-per-page {
    width: rem(60);
  }
}
</style>
